import { skipToken, useMutation } from '@apollo/client'
import { Loader } from 'lucide-react'
import { Suspense, useTransition } from 'react'
import { toast } from 'sonner'
import { ToasterContent } from '@/components/sv/toaster-content'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { gql } from '@/lib/graphql/gql'
import { useIdCompany, useSuspenseQuery } from '@/lib/hooks'
import { ToggleJitDialogFallback } from '@/routes/single-sign-on/jit/toggle-jit-dialog/fallback'
import type {
  ToggleJitSsoQuery,
  ToggleJitSsoQueryVariables,
  ToggleJitMutation,
  ToggleJitMutationVariables,
} from '@/routes/single-sign-on/jit/toggle-jit-dialog/index.types.gen'

const TOGGLE_JIT_SSO_QUERY = gql`
  query ToggleJitSso($idCompany: String!) {
    company(id: $idCompany) {
      id
      samlSetting @live {
        id
        isJitEnabled
        jitDefaultRoles @live {
          id
          idRole
          type
          idSite
        }
      }
      draftSamlSetting: samlSetting(isDraft: true) @live {
        id
        isJitEnabled
        jitDefaultRoles @live {
          id
          idRole
          type
          idSite
        }
      }
    }
  }
`

const TOGGLE_JIT_MUTATION = gql`
  mutation ToggleJit($idCompany: String!, $isEnabled: Boolean!) {
    toggleJit(idCompany: $idCompany, isEnabled: $isEnabled) {
      id
      isJitEnabled
    }
  }
`

type Props = {
  isOpen: boolean
  onClose: () => void
}

const DialogContent = ({ isOpen, onClose }: Props) => {
  const { idCompany } = useIdCompany()
  const [isPending, startTransition] = useTransition()

  const { data } = useSuspenseQuery<
    ToggleJitSsoQuery,
    ToggleJitSsoQueryVariables
  >(TOGGLE_JIT_SSO_QUERY, isOpen ? { variables: { idCompany } } : skipToken)

  const liveSamlSetting = data?.company?.samlSetting
  const draftSamlSetting = data?.company?.draftSamlSetting
  const isEnabled =
    liveSamlSetting?.isJitEnabled ?? draftSamlSetting?.isJitEnabled ?? false

  const [toggleJit] = useMutation<
    ToggleJitMutation,
    ToggleJitMutationVariables
  >(TOGGLE_JIT_MUTATION)

  return (
    <>
      <AlertDialogHeader>
        <AlertDialogTitle>
          {isEnabled ? 'Disable JIT provisioning' : 'Enable JIT provisioning'}
        </AlertDialogTitle>
        <AlertDialogDescription>
          {isEnabled ? (
            <>
              Once disabled, only users already added to your Sevalla account
              will be able to access it. If you want new users to join, you will
              need to send them an invite.
              <br />
              <br />
              No existing users will be removed. If you want any users to not
              have access to this account anymore, you need to remove them on
              the User management page.
            </>
          ) : (
            <>
              After enabling JIT, users don't need to be invited to the company
              account manually. Anyone allowed by the identity provider's
              settings will be able to join in a self-served way. Typically,
              that means anyone who has an email address with a specified
              domain.
              <br />
              <br />
              Users joining this way will be assigned the default JIT role that
              is set up here. You can still configure different access levels
              for each user after they've joined.
            </>
          )}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel disabled={isPending} onClick={() => onClose()}>
          Cancel
        </AlertDialogCancel>
        <AlertDialogAction
          disabled={isPending}
          onClick={() => {
            startTransition(async () => {
              try {
                await toggleJit({
                  variables: { idCompany, isEnabled: !isEnabled },
                })
                toast(
                  <ToasterContent variant="success">
                    JIT {isEnabled ? 'disabled' : 'enabled'} successfully
                  </ToasterContent>,
                  { position: 'top-right' }
                )
                onClose()
              } catch {
                toast(
                  <ToasterContent variant="error">
                    Failed to {isEnabled ? 'disable' : 'enable'} JIT
                  </ToasterContent>,
                  { position: 'top-right' }
                )
              }
            })
          }}
        >
          {isPending ? <Loader className="animate-spin" /> : null}
          Continue
        </AlertDialogAction>
      </AlertDialogFooter>
    </>
  )
}

export const ToggleJitDialog = ({ isOpen, onClose }: Props) => {
  return (
    <AlertDialog open={isOpen}>
      <AlertDialogContent onEscapeKeyDown={onClose}>
        <Suspense fallback={<ToggleJitDialogFallback />}>
          <DialogContent isOpen={isOpen} onClose={onClose} />
        </Suspense>
      </AlertDialogContent>
    </AlertDialog>
  )
}
